import {ChangeDetectionStrategy, Component, computed, inject, input, output, signal} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {toSignal} from '@angular/core/rxjs-interop';
import {Store} from '@ngrx/store';
import {Tracker, TrackerMetric} from 'src/app/core/model/tracker';
import {AddMetricToTrackerAction} from 'src/app/core/store/trackers/trackers.action';
import {MessageService} from 'src/app/services/message.service';
import {OphButtonModule} from 'src/app/shared/design/oph-button/oph-button.module';
import {OphBackButtonComponent} from 'src/app/shared/design/oph-back-button/oph-back-button.component';
import {TrackersService} from 'src/app/shared/services/trackers.service';
import {TrackerDetailMetricComponent} from 'src/app/trackers/tracker-detail/metrics/metric/tracker-detail-metric.component';

@Component({
  selector: 'projects-task-subtask-new-metric',
  standalone: true,
  imports: [TrackerDetailMetricComponent, OphButtonModule, OphBackButtonComponent],
  templateUrl: './projects-task-subtask-new-metric.component.html',
  styleUrl: './projects-task-subtask-new-metric.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectsTaskSubtaskNewMetricComponent {
  trackerId = input.required<string>();
  back = output<void>();
  metricAdded = output<Tracker>();

  private readonly trackersService = inject(TrackersService);
  private readonly store = inject(Store);
  private readonly messageService = inject(MessageService);

  readonly loading = signal(false);
  readonly metricForm: FormGroup = this.trackersService.getMetricsFormGroup({} as TrackerMetric);

  // Convert form value changes to a signal
  private readonly formValue = toSignal(this.metricForm.valueChanges, {initialValue: this.metricForm.value});

  // Compute validity based on the reactive form value
  readonly valid = computed(() => this.trackersService.isMetricFormValid(this.formValue()));

  onBack() {
    this.back.emit();
  }

  onSave() {
    this.loading.set(true);
    const metricDto = this.trackersService.formatMetricDto(this.metricForm);

    this.store.dispatch(
      new AddMetricToTrackerAction({
        trackerId: this.trackerId(),
        metric: metricDto,
        onSuccess: tracker => this.onSuccess(tracker),
        onFailure: err => this.onFailure(err),
      })
    );
  }

  private onSuccess(tracker: Tracker) {
    this.loading.set(false);
    this.metricAdded.emit(tracker);
  }

  private onFailure(err: Error) {
    this.loading.set(false);
    this.messageService.add(err.message || 'There was a problem adding the metric.');
  }
}
